import { createRef, h } from "preact";
import { beforeUnmount, useLayoutEffect, useState } from "preact/hooks";

import { classNames, humanFileSize } from "../helpers.js";
import i18n from "../i18n.js";
import Button from "./Button.jsx";
import LinearLoadingIndicator from "./LinearLoadingIndicator.jsx";

import TextField from "./TextField.jsx";
import Icon from "./Icon.jsx";

export default function UploadField({
  dragDrop,
  dragDropAllow,
  onChange,
  uploading,
  size,
  imageUpload,
  icon,
  error,
  ...props
}) {
  const boxRef = createRef();
  const inputRef = createRef();
  const [draggedOver, setDraggedOver] = useState(false);

  function handleInput(event) {
    if (event.target.files.length === 0) {
      return false;
    }
    onChange && onChange(event.target.files[0]);
  }

  function openUploadDialog(event) {
    event.preventDefault();
    inputRef.current.click();
  }

  function handleDragEnter(event) {
    // Prevent default behavior (Prevent file from being opened) on dragover
    event.preventDefault();

    const files = event.dataTransfer.items;
    if (files.length === 0) return;
    if (dragDropAllow && !dragDropAllow.includes(files[0].type)) return;

    setDraggedOver(true);
  }

  function handleDragLeave(event) {
    setDraggedOver(false);
  }

  function handleDrop(event) {
    event.preventDefault();
    const files = event.dataTransfer.files;
    onChange && onChange(files[0]);
  }

  useLayoutEffect(() => {
    if (!dragDrop) return;

    const dropZone = boxRef.current;
    dropZone.addEventListener("dragenter", handleDragEnter, false);
    dropZone.addEventListener("dragover", handleDragEnter, false);
    dropZone.addEventListener("dragleave", handleDragLeave, false);
    dropZone.addEventListener("drop", handleDrop, false);

    return () => {
      dropZone.removeEventListener("dragenter", handleDragEnter);
      dropZone.removeEventListener("dragover", handleDragEnter);
      dropZone.removeEventListener("dragleave", handleDragLeave);
      dropZone.removeEventListener("drop", handleDrop);
    };
  }, []);

  return (
    <div class="uploadField">
      <div
        class={classNames(
          "uploadField__uploadBox",
          draggedOver && "uploadField__uploadBox--draggedOver"
        )}
        ref={boxRef}
      >
        {icon ? (
          <Icon big>{icon}</Icon>
        ) : imageUpload ? (
          <img src="/images/avatar_upload.svg" width="192" height="192" />
        ) : (
          <img src="/images/cloud_upload.svg" width="128" height="128" />
        )}

        {imageUpload ? (
          <p class="mt-0 mb-24">
            <Button
              iconFront="file_upload"
              onClick={openUploadDialog}
              mint={draggedOver}
            >
              {i18n.t("component.upload_field.upload_button")}
            </Button>
          </p>
        ) : draggedOver ? (
          <p class="t-body3 mt-0 mb-24 color-brand-jade">
            {i18n.t("component.upload_field.desc_dragged_over")}
          </p>
        ) : (
          <p class="t-body3 mt-0 mb-24">
            <a href="#" onClick={openUploadDialog}>
              {i18n.t("component.upload_field.desc_click")}
            </a>{" "}
            {i18n.t("component.upload_field.desc_drag")}
          </p>
        )}
        {error && <p class="t-caption color-semantic-error">{error}</p>}
        <p class="t-caption color-shades-grey">
          {i18n.t("component.upload_field.max_size", {
            size: humanFileSize(size),
          })}
        </p>
      </div>
      <TextField
        type="file"
        ref={inputRef}
        class="uploadField__hiddenInput"
        {...props}
        onInput={handleInput}
      />
      {uploading && <LinearLoadingIndicator class="uploadField__loading" />}
    </div>
  );
}
